import { Trans, useTranslation } from 'react-i18next'

import { Styles as S } from '~/pages/anatomy/proofGraph/styles'
import { useNextStep } from '~/shared/hooks/useNextStep'
import { TPageProps } from '~/shared/types/commonTypes'

const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000

const getTargetDate = (locale: string) => {
  const date = new Date(Date.now() + THIRTY_DAYS_MS)
  const month = date.toLocaleDateString(locale, { month: 'long' })
  const day = date.getDate()
  const year = date.getFullYear()
  return `${month}, ${day}, ${year}`
}

const HIGHLIGHT_COLOR = '#8C67F6'

const CURVE_COLOR = '#0071FF'
const LESSONS_COLOR = '#FF8D00'
const DOT_RING = 'white'

const CURVE_PATH =
  'M8 166 C58 145 77 166 84 162 C115 163 154 121 154 122 C154 124 169 98 195 100 C238 103 260 55 289 42 C287 38 325 26 330 27'

type TDotProps = {
  cx: number
  cy: number
  fill: string
}

const ChartDot = ({ cx, cy, fill }: TDotProps) => (
  <>
    <circle cx={cx} cy={cy} r="9" fill={DOT_RING} />
    <circle cx={cx} cy={cy} r="6.5" fill={fill} />
  </>
)

const PersonalPlanArt = ({ nextPagePath, pageId }: TPageProps) => {
  const { t, i18n } = useTranslation()

  const handleContinue = useNextStep({
    nextPagePath,
    pageId,
    pageName: pageId,
    question: `We predict you'll feel confident in your art knowledge`,
  })

  const targetDate = getTargetDate(i18n.language)

  return (
    <S.Root>
      <S.StyledContainer>
        <S.Title>
          <Trans
            ns="art"
            i18nKey="onboarding.artPersonalPlan.title"
            values={{ date: targetDate }}
            components={{
              highlight: (
                <span
                  style={{ color: HIGHLIGHT_COLOR, fontWeight: 'inherit' }}
                />
              ),
            }}
          />
        </S.Title>

        <S.ChartCard>
          <svg
            viewBox="0 0 330 220"
            xmlns="http://www.w3.org/2000/svg"
            style={{
              width: '100%',
              height: 'auto',
              display: 'block',
              overflow: 'visible',
            }}
          >
            <line
              x1="8"
              y1="38"
              x2="322"
              y2="38"
              stroke="#E8ECF4"
              strokeWidth="1"
            />
            <line
              x1="8"
              y1="70"
              x2="322"
              y2="70"
              stroke="#E8ECF4"
              strokeWidth="1"
            />
            <line
              x1="8"
              y1="102"
              x2="322"
              y2="102"
              stroke="#E8ECF4"
              strokeWidth="1"
            />
            <line
              x1="8"
              y1="134"
              x2="322"
              y2="134"
              stroke="#E8ECF4"
              strokeWidth="1"
            />
            <line
              x1="8"
              y1="166"
              x2="322"
              y2="166"
              stroke="#E8ECF4"
              strokeWidth="1"
            />
            <line
              x1="8"
              y1="198"
              x2="322"
              y2="198"
              stroke="#E8ECF4"
              strokeWidth="1"
            />

            <S.AnimatedPath
              d={CURVE_PATH}
              pathLength="1"
              fill="none"
              stroke={CURVE_COLOR}
              strokeWidth="4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />

            <line
              x1="8"
              y1="168"
              x2="8"
              y2="198"
              stroke={CURVE_COLOR}
              strokeWidth="2.5"
              strokeDasharray="4 3"
            />

            <ChartDot cx={8} cy={166} fill={CURVE_COLOR} />

            <S.HabitFormedGroup>
              <ChartDot cx={152} cy={122} fill={CURVE_COLOR} />
              <rect
                x="110"
                y="81"
                width="84"
                height="22"
                rx="11"
                fill={CURVE_COLOR}
              />
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M0.300781 0C1.99516 0 3.61055 0.716408 4.74797 1.97228L9.9594 7.72645C11.5472 9.47958 14.3012 9.47958 15.889 7.72645L21.1004 1.97228C22.2378 0.716408 23.8532 0 25.5476 0H26L0 0H0.300781Z"
                fill={CURVE_COLOR}
                transform="translate(139, 102)"
              />
              <text
                x="152"
                y="92"
                textAnchor="middle"
                dominantBaseline="middle"
                fill="white"
                fontSize="10"
                style={{ fontWeight: 700 }}
              >
                {t('onboarding.personalPlan.habitFormed')}
              </text>
            </S.HabitFormedGroup>

            <S.LessonsGroup>
              <ChartDot cx={289} cy={42} fill={LESSONS_COLOR} />
              <rect
                x="239"
                y="3"
                width="100"
                height="22"
                rx="11"
                fill={LESSONS_COLOR}
              />
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M0.300781 0C1.99516 0 3.61055 0.716408 4.74797 1.97228L9.9594 7.72645C11.5472 9.47958 14.3012 9.47958 15.889 7.72645L21.1004 1.97228C22.2378 0.716408 23.8532 0 25.5476 0H26L0 0H0.300781Z"
                fill={LESSONS_COLOR}
                transform="translate(276, 24)"
              />
              <text
                x="289"
                y="14"
                textAnchor="middle"
                dominantBaseline="middle"
                fill="white"
                fontSize="10"
                style={{ fontWeight: 700 }}
              >
                {t('onboarding.personalPlan.lessonsDone')}
              </text>
            </S.LessonsGroup>

            <text
              x="8"
              y="206"
              textAnchor="middle"
              dominantBaseline="hanging"
              fill={CURVE_COLOR}
              fontSize="12"
              letterSpacing="0.5"
              style={{ fontWeight: 600 }}
            >
              {t('onboarding.personalPlan.week1')}
            </text>
            <text
              x="322"
              y="206"
              textAnchor="end"
              dominantBaseline="hanging"
              fill={CURVE_COLOR}
              fontSize="12"
              letterSpacing="0.5"
              style={{ fontWeight: 600 }}
            >
              {t('onboarding.personalPlan.week4')}
            </text>
          </svg>
        </S.ChartCard>

        <S.InfoSection>
          <p>
            <Trans ns="art" i18nKey="onboarding.artPersonalPlan.infoHabit" />
          </p>
          <p>
            <Trans ns="art" i18nKey="onboarding.artPersonalPlan.infoTime" />
          </p>
        </S.InfoSection>

        <S.StyledButton type="button" onClick={() => handleContinue('')}>
          <Trans i18nKey="actions.continue" />
        </S.StyledButton>
      </S.StyledContainer>
    </S.Root>
  )
}

export default PersonalPlanArt
